import React from 'react';
import { Link } from 'react-router-dom';

import { Badge, Callout, Step } from './shared';

const GitReviewer: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-2">Git Reviewer</h2>
    <p className="text-base-content/50 text-sm mb-5">An AI reviewer that reads your pull requests for you.</p>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      The Git Reviewer watches the GitHub repos you&apos;ve connected and leaves a real GitHub review on open pull
      requests. It checks each PR against your project&apos;s{' '}
      <Link to="/docs/knowledge-base" className="link link-primary">
        Knowledge Base
      </Link>{' '}
      and your architecture decisions — both the ADRs stored in MFlow and any in the repo&apos;s{' '}
      <code className="bg-base-200 px-1 rounded text-xs">docs/adr/</code> folder — so a review can say &quot;this goes
      against the decision you made&quot;, not just point out style issues.
    </p>

    <h3 className="font-semibold mb-3">Turning it on</h3>
    <div className="space-y-3 mb-6">
      <Step n={1}>
        Connect GitHub from <strong>Integrations</strong> — the reviewer uses that same connection to read PRs and post
        reviews.
      </Step>
      <Step n={2}>
        Open <strong>Settings → Automation</strong> and switch on <strong>Git Reviewer agent</strong>. It&apos;s off by
        default.
      </Step>
      <Step n={3}>
        Open <strong>Git Reviewer</strong> in the sidebar to see every review it has run.
      </Step>
    </div>

    <h3 className="font-semibold mb-2">When reviews run</h3>
    <ul className="text-sm text-base-content/70 space-y-2 list-disc list-inside mb-4 leading-relaxed">
      <li>
        <strong>Automatically, every 10 minutes</strong> — while the setting is on, MFlow checks your connected repos
        for open PRs and reviews any it hasn&apos;t seen yet.
      </li>
      <li>
        <strong>On demand</strong> — from the Git Reviewer page, click <strong>Run all eligible now</strong> to review
        every eligible PR straight away, or enter an{' '}
        <code className="bg-base-200 px-1 rounded text-xs">owner/repo</code> and PR number and click{' '}
        <strong>Run review</strong> for one PR. Each row in the history also has its own <strong>Run review</strong>{' '}
        button.
      </li>
    </ul>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      A PR is reviewed once per commit: it&apos;s only reviewed again after someone pushes new commits. Several PRs can
      be reviewed at the same time.
    </p>

    <Callout type="info">
      A manual run may skip some PRs. The page lists each skipped PR and the reason, for example{' '}
      <em>already reviewed at a1b2c3d</em> (no new commits), <em>already queued or running</em>, <em>PR is closed</em>,
      or <em>the connected GitHub account can&apos;t read this repo</em>. A skip isn&apos;t an error — nothing was wrong
      with the PR.
    </Callout>

    <h3 className="font-semibold mb-2 mt-6">What a review looks like</h3>
    <p className="text-sm text-base-content/70 mb-3 leading-relaxed">
      Each review ends with one of three GitHub verdicts:
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
      {[
        { label: 'approve', color: 'badge-success', desc: 'Nothing blocking — the PR looks good to merge.' },
        {
          label: 'request_changes',
          color: 'badge-error',
          desc: 'Something should be fixed first, such as a bug or a conflict with an accepted ADR.',
        },
        { label: 'comment', color: 'badge-info', desc: 'Notes and suggestions, but no approval or block.' },
      ].map((v) => (
        <div key={v.label} className="rounded-xl bg-base-200 shadow-card p-4 space-y-1.5">
          <Badge color={v.color}>{v.label}</Badge>
          <div className="text-xs text-base-content/60 leading-relaxed">{v.desc}</div>
        </div>
      ))}
    </div>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Along with the verdict, the reviewer writes a summary comment and, where useful, findings pinned to a specific
      file and line.
    </p>

    <Callout type="tip">
      <strong>Reviewing your own PRs:</strong> GitHub won&apos;t let an account approve or request changes on its own
      pull request. If the PR author is the same GitHub account MFlow reviews as, the reviewer posts a plain comment
      instead, with the verdict (Approve, Requested changes, or Comment) written at the top. The Git Reviewer page
      labels these as <em>(Self-PR)</em>.
    </Callout>

    <h3 className="font-semibold mb-2 mt-6">The Git Reviewer page</h3>
    <ul className="text-sm text-base-content/70 space-y-2 list-disc list-inside mb-4 leading-relaxed">
      <li>
        <strong>Review history</strong> — every run, newest first, with the repo, PR number and title, the commit that
        was reviewed, a link to the PR, and its status (<Badge>queued</Badge> <Badge color="badge-info">running</Badge>{' '}
        <Badge color="badge-success">completed</Badge> <Badge color="badge-error">failed</Badge>).
      </li>
      <li>
        <strong>Live updates</strong> — status and verdict update on the page as the review runs, so there&apos;s no
        need to refresh.
      </li>
      <li>
        <strong>Details</strong> — click a run to see the full review comment, the file-level findings, or the error if
        the run failed.
      </li>
      <li>
        <strong>ADR flags</strong> — a review that mentions ADRs or drift is highlighted, so conflicts with your
        architecture decisions stand out.
      </li>
    </ul>

    <h3 className="font-semibold mb-2">Which AI model it uses</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Reviews run on MFlow&apos;s hosted model by default. If you&apos;ve set a default provider under{' '}
      <strong>Settings → AI agents</strong>, the reviewer uses that key instead (see{' '}
      <Link to="/docs/ai-providers" className="link link-primary">
        AI Providers &amp; Keys
      </Link>
      ).
    </p>

    <Callout type="info">
      The Git Reviewer only reads. It never edits your Knowledge Base, never writes to the repo&apos;s{' '}
      <code className="bg-base-200 px-1 rounded text-xs">docs/adr/</code> folder, and never pushes code. It&apos;s
      separate from the{' '}
      <Link to="/docs/coding-agent" className="link link-primary">
        Coding Agent
      </Link>
      , which tracks review feedback on the PRs it opened itself.
    </Callout>
  </div>
);

export default GitReviewer;
